import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useAuth } from '../../auth/AuthContext';
import { useHospitalDirectory } from '../../hooks/useHospitalDirectory';
import { useHmsConfig } from '../../hooks/useHmsRecords';
import { useEmrSettings } from '../../hooks/useEmrSettings';
import { Q, queueRowId } from '../../config/queueStatus';
import { slotBucketsForDoctor } from '../../config/emrSettings';
import { doctorHasDentalWorkspace, isDentalSpecialty } from '../../utils/dentalSpecialty';
import { DENTAL_CHAIRS, DENTAL_SLOT_TIMES } from '../../utils/dentalFees';

// ─────────────────────────────────────────────
// Static slot fallbacks (when Rx pad doctor availability not configured)
// ─────────────────────────────────────────────

const MORNING_SLOTS = ['09:00', '09:20', '09:40', '10:00', '10:20', '10:40', '11:00', '11:20', '11:40', '12:00', '12:20', '12:40'];
const EVENING_SLOTS = ['16:00', '16:20', '16:40', '17:00', '17:20', '17:40', '18:00', '18:20', '18:40', '19:00', '19:20', '19:40'];

// Weekly calendar — current ISO week (Mon–Sun)
function buildWeekDays(anchor = new Date()) {
  const day = anchor.getDay();
  const monday = new Date(anchor);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(anchor.getDate() - ((day + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      label: d.toLocaleDateString('en-IN', { weekday: 'short' }),
      date: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
      dateStr: d.toISOString().slice(0, 10),
    };
  });
}

function weekRangeLabel(days) {
  if (!days?.length) return '';
  const first = new Date(days[0].dateStr);
  const last = new Date(days[days.length - 1].dateStr);
  const fmt = (dt) => dt.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(first)} – ${fmt(last)}`;
}

// ─────────────────────────────────────────────
// Helper Utilities
// ─────────────────────────────────────────────

function getTodayStr() {
  return new Date().toISOString().slice(0, 10);
}

function aptStatusFromQueue(s) {
  if (s === Q.COMPLETED) return 'Done';
  if (s === Q.IN_CONSULTATION) return 'Consulting';
  if (s === Q.AWAITING_PAYMENT) return 'Awaiting Payment';
  if (s === Q.WAITING_DOCTOR) return 'Waiting for Doctor';
  if (s === Q.CHECKED_IN) return 'Checked-in';
  return 'Booked';
}

function queueStatusFromApt(s) {
  if (s === 'Done') return Q.COMPLETED;
  if (s === 'Consulting') return Q.IN_CONSULTATION;
  if (s === 'Awaiting Payment') return Q.AWAITING_PAYMENT;
  if (s === 'Waiting for Doctor') return Q.WAITING_DOCTOR;
  if (s === 'Checked-in') return Q.CHECKED_IN;
  return Q.BOOKED;
}

function queueRowToAppointment(row) {
  return {
    id: queueRowId(row),
    token: row.token,
    time: row.appointmentTime || row.time || '09:00',
    patient: row.patientName,
    phone: row.patientPhone || '',
    uhid: row.patientId || '',
    doctor: row.doctor,
    dept: '',
    type: row.visitType || 'Walk-in',
    status: aptStatusFromQueue(row.status),
    complaint: row.reason || '',
    date: row.visitDate || (row.createdAt ? row.createdAt.slice(0, 10) : getTodayStr()),
    chair: row.chair || (String(row.service || '').match(/^Chair\s+\d/i) ? row.service : ''),
  };
}

function formatDisplayTime(t) {
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayH = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayH}:${m} ${ampm}`;
}

// ─────────────────────────────────────────────
// Sub-Components
// ─────────────────────────────────────────────

/* ── Visit Type Badge ── */
function VisitTypeBadge({ type }) {
  const styles = {
    New:       { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd' },
    'Follow-up':{ background: '#dcfce7', color: '#15803d', border: '1px solid #86efac' },
    Emergency: { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' },
    'Walk-in': { background: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd' },
  };
  const s = styles[type] || { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' };
  return (
    <span style={{ ...s, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {type}
    </span>
  );
}

/* ── Status Badge ── */
function StatusBadge({ status }) {
  const map = {
    Booked:      { bg: '#f0f9ff', color: '#0369a1', border: '#7dd3fc' },
    'Checked-in':{ bg: '#fefce8', color: '#a16207', border: '#fde047' },
    'Awaiting Payment': { bg: '#fff7ed', color: '#c2410c', border: '#fb923c' },
    'Waiting for Doctor': { bg: '#ede9fe', color: '#6d28d9', border: '#c4b5fd' },
    Consulting:  { bg: '#fff7ed', color: '#c2410c', border: '#fb923c' },
    Done:        { bg: '#f0fdf4', color: '#166534', border: '#86efac' },
    'No-show':   { bg: '#fef2f2', color: '#9f1239', border: '#fca5a5' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
}

/* ── Stat Mini Card ── */
function StatCard({ label, count, color }) {
  return (
    <div style={{ background: '#fff', border: `2px solid ${color}20`, borderRadius: 12, padding: '14px 20px', minWidth: 110, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, boxShadow: `0 2px 8px ${color}15` }}>
      <span style={{ fontSize: 28, fontWeight: 800, color }}>{count}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
  );
}

// ─────────────────────────────────────────────
// Tab 1: Today's Appointments
// ─────────────────────────────────────────────

function TodayTab({ appointments, onStatusChange }) {
  const todayApts = useMemo(
    () => appointments.filter(a => a.date === getTodayStr()),
    [appointments]
  );

  const stats = useMemo(() => {
    const counts = {
      Booked: 0, 'Checked-in': 0, 'Awaiting Payment': 0, 'Waiting for Doctor': 0,
      Consulting: 0, Done: 0, 'No-show': 0,
    };
    todayApts.forEach((a) => { if (counts[a.status] !== undefined) counts[a.status]++; });
    return counts;
  }, [todayApts]);

  const statConfig = [
    { label: 'Booked', key: 'Booked', color: '#0ea5e9' },
    { label: 'Checked-in', key: 'Checked-in', color: '#eab308' },
    { label: 'Awaiting bill', key: 'Awaiting Payment', color: '#f97316' },
    { label: 'Waiting', key: 'Waiting for Doctor', color: '#8b5cf6' },
    { label: 'Consulting', key: 'Consulting', color: '#f97316' },
    { label: 'Done', key: 'Done', color: '#22c55e' },
  ];

  function nextAction(status) {
    if (status === 'Booked') return { label: 'Check-in', next: 'Checked-in' };
    if (status === 'Checked-in') return { label: 'Awaiting bill', next: 'Awaiting Payment' };
    if (status === 'Awaiting Payment') return { label: 'Bill at Q Page', link: '/reception/queue' };
    if (status === 'Waiting for Doctor') return { label: 'With doctor', hint: true };
    if (status === 'Consulting') return { label: 'Mark Done', next: 'Done' };
    return null;
  }

  return (
    <div>
      {/* Stats Row */}
      <div className="stat-grid" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        {statConfig.map(s => (
          <StatCard key={s.key} label={s.label} count={stats[s.key]} color={s.color} />
        ))}
      </div>

      {/* Info Banner */}
      <div className="info-banner" style={{ marginBottom: 16 }}>
        <span style={{ marginRight: 6 }}>📅</span>
        Showing <strong>{todayApts.length}</strong> appointments for today — {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px #0001' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)' }}>
              {['Token #', 'Time', 'Patient Name', 'Phone', 'UHID', 'Doctor', 'Dept', 'Visit Type', 'Status', 'Actions'].map(col => (
                <th key={col} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {todayApts.map((apt, idx) => {
              const action = nextAction(apt.status);
              return (
                <tr key={apt.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8fafc'}>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ background: '#6366f1', color: '#fff', borderRadius: '50%', width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>{apt.token}</span>
                  </td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>{formatDisplayTime(apt.time)}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{apt.patient}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{apt.complaint}</div>
                  </td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', color: '#475569', fontFamily: 'monospace' }}>{apt.phone}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}>
                    <span className="emr-chip" style={{ background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, fontFamily: 'monospace' }}>{apt.uhid}</span>
                  </td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', fontWeight: 500, whiteSpace: 'nowrap' }}>{apt.doctor}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: 12 }}>{apt.dept}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}><VisitTypeBadge type={apt.type} /></td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}><StatusBadge status={apt.status} /></td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}>
                    {action?.link ? (
                      <Link to={action.link} className="btn btn-primary btn-sm" style={{ fontSize: 12, padding: '5px 12px', borderRadius: 8, whiteSpace: 'nowrap', textDecoration: 'none' }}>
                        {action.label}
                      </Link>
                    ) : action?.hint ? (
                      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>In doctor queue</span>
                    ) : action ? (
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ fontSize: 12, padding: '5px 12px', borderRadius: 8, whiteSpace: 'nowrap', cursor: 'pointer', background: action.next === 'Checked-in' ? '#0ea5e9' : action.next === 'Awaiting Payment' ? '#f97316' : action.next === 'Consulting' ? '#f97316' : '#22c55e', border: 'none', color: '#fff', fontWeight: 600, transition: 'opacity 0.15s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                        onClick={() => onStatusChange(apt.id, action.next)}
                      >
                        {action.label}
                      </button>
                    ) : apt.status === 'Done' ? (
                      <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>✓ Done</span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Tab 2: Book Appointment
// ─────────────────────────────────────────────

const EMPTY_FORM = {
  patient: '', phone: '', uhid: '', doctor: '', date: getTodayStr(),
  timeSlot: '', visitType: 'New', complaint: '',
};

const BOOK_STEPS = [
  { id: 1, label: 'Slot' },
  { id: 2, label: 'Type' },
  { id: 3, label: 'Patient' },
  { id: 4, label: 'Book' },
];

function SlotChip({ time, selected, taken, onClick }) {
  return (
    <button
      type="button"
      disabled={taken}
      onClick={onClick}
      style={{
        padding: '10px 12px',
        borderRadius: 10,
        border: selected ? '2px solid #0d6e6a' : '1.5px solid #e2e8f0',
        background: taken ? '#f1f5f9' : selected ? '#f0fdfa' : '#fff',
        color: taken ? '#94a3b8' : selected ? '#0f766e' : '#1e293b',
        fontWeight: 700,
        fontSize: 13,
        cursor: taken ? 'not-allowed' : 'pointer',
        opacity: taken ? 0.55 : 1,
      }}
    >
      {formatDisplayTime(time)}{taken ? ' · booked' : ''}
    </button>
  );
}

function BookTab({ onBook, doctors = [], appointments = [], emrSettings = {} }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [patientHits, setPatientHits] = useState([]);
  const [searching, setSearching] = useState(false);

  const docList = doctors.length ? doctors : [];

  const { morning: morningSlots, evening: eveningSlots } = useMemo(() => slotBucketsForDoctor(
    emrSettings.doctorAvailability,
    form.doctor,
    { morningFallback: MORNING_SLOTS, eveningFallback: EVENING_SLOTS },
  ), [emrSettings.doctorAvailability, form.doctor]);

  const takenSlots = useMemo(() => {
    if (!form.doctor || !form.date) return new Set();
    return new Set(
      (appointments || [])
        .filter((a) => a.doctor === form.doctor && a.date === form.date && a.status !== 'Done')
        .map((a) => a.time)
    );
  }, [appointments, form.doctor, form.date]);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    if (success) setSuccess('');
  }

  async function searchPatients(q) {
    const query = String(q || '').trim();
    if (query.length < 2) { setPatientHits([]); return; }
    setSearching(true);
    try {
      const res = await ipApi.getPatients(query, 12);
      setPatientHits(res?.patients || res?.items || []);
    } catch (e) {
      setPatientHits([]);
      setErrors((prev) => ({ ...prev, patient: e.message || 'Patient search failed — check connection' }));
    } finally {
      setSearching(false);
    }
  }

  function pickPatient(p) {
    setForm((prev) => ({
      ...prev,
      patient: p.name || p.patientName || '',
      phone: String(p.phone || p.mobile || '').replace(/\D/g, '').slice(0, 10),
      uhid: p.patientId || p.uhid || '',
    }));
    setPatientHits([]);
  }

  function validateStep(s) {
    const errs = {};
    if (s === 1) {
      if (!form.doctor) errs.doctor = 'Select a doctor.';
      if (!form.date) errs.date = 'Select a date.';
      if (!form.timeSlot) errs.timeSlot = 'Select an open slot.';
      else if (takenSlots.has(form.timeSlot)) errs.timeSlot = 'That slot is already booked.';
    }
    if (s === 2) {
      if (!form.visitType) errs.visitType = 'Select visit type.';
    }
    if (s === 3) {
      if (!form.patient.trim()) errs.patient = 'Patient name is required.';
      if (!form.phone.trim()) errs.phone = 'Phone is required.';
      else if (!/^\d{10}$/.test(form.phone.trim())) errs.phone = 'Enter a valid 10-digit phone.';
    }
    return errs;
  }

  function goNext() {
    const errs = validateStep(step);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep((s) => Math.min(4, s + 1));
  }

  async function handleBook() {
    const errs = { ...validateStep(1), ...validateStep(2), ...validateStep(3) };
    if (!form.complaint.trim()) errs.complaint = 'Chief complaint helps the doctor.';
    if (Object.keys(errs).length) { setErrors(errs); setStep(errs.patient || errs.phone ? 3 : errs.visitType ? 2 : 1); return; }
    const doctorObj = docList.find((d) => d.name === form.doctor);
    setBusy(true);
    try {
      await onBook({
        time: form.timeSlot,
        patient: form.patient.trim(),
        phone: form.phone.trim(),
        uhid: form.uhid.trim() || '',
        doctor: form.doctor,
        dept: doctorObj?.dept || doctorObj?.specialty || '',
        type: form.visitType,
        complaint: form.complaint.trim(),
        date: form.date,
      });
      setSuccess(`Booked · ${form.patient} · ${form.doctor} · ${form.date} ${formatDisplayTime(form.timeSlot)}`);
      setForm(EMPTY_FORM);
      setErrors({});
      setStep(1);
    } catch {
      /* parent surfaces error */
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="info-banner" style={{ marginBottom: 16 }}>
        Book appointment: <strong>Slot → Type → Patient → Book</strong>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {BOOK_STEPS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`emr-chip ${step === s.id ? 'active' : ''}`}
            onClick={() => setStep(s.id)}
          >
            {s.id}. {s.label}
          </button>
        ))}
      </div>

      {success && (
        <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '12px 16px', color: '#166534', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>
          {success}
        </div>
      )}

      <div className="card" style={{ padding: 24, borderRadius: 14 }}>
        {step === 1 && (
          <>
            <h3 style={{ marginTop: 0 }}>1 · Select slot</h3>
            <div className="form-grid" style={{ marginBottom: 16 }}>
              <div className="field">
                <label>Doctor *</label>
                <select className="input" value={form.doctor} onChange={(e) => { handleChange('doctor', e.target.value); handleChange('timeSlot', ''); }}>
                  <option value="">— Select doctor —</option>
                  {docList.map((d) => (
                    <option key={d.name} value={d.name}>{d.name}{d.dept || d.specialty ? ` (${d.dept || d.specialty})` : ''}</option>
                  ))}
                </select>
                {errors.doctor && <div className="error">{errors.doctor}</div>}
              </div>
              <div className="field">
                <label>Date *</label>
                <input className="input" type="date" min={getTodayStr()} value={form.date} onChange={(e) => { handleChange('date', e.target.value); handleChange('timeSlot', ''); }} />
                {errors.date && <div className="error">{errors.date}</div>}
              </div>
            </div>
            <div style={{ marginBottom: 10, fontWeight: 700, fontSize: 13, color: '#475569' }}>Morning</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))', gap: 8, marginBottom: 16 }}>
              {morningSlots.map((s) => (
                <SlotChip key={s} time={s} selected={form.timeSlot === s} taken={takenSlots.has(s)} onClick={() => handleChange('timeSlot', s)} />
              ))}
            </div>
            <div style={{ marginBottom: 10, fontWeight: 700, fontSize: 13, color: '#475569' }}>Evening</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))', gap: 8 }}>
              {eveningSlots.map((s) => (
                <SlotChip key={s} time={s} selected={form.timeSlot === s} taken={takenSlots.has(s)} onClick={() => handleChange('timeSlot', s)} />
              ))}
            </div>
            {errors.timeSlot && <div className="error" style={{ marginTop: 10 }}>{errors.timeSlot}</div>}
          </>
        )}

        {step === 2 && (
          <>
            <h3 style={{ marginTop: 0 }}>2 · Visit type</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
              {['New', 'Follow-up', 'Walk-in', 'Emergency'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleChange('visitType', t)}
                  style={{
                    padding: '16px 12px',
                    borderRadius: 12,
                    border: form.visitType === t ? '2px solid #0d6e6a' : '1.5px solid #e2e8f0',
                    background: form.visitType === t ? '#f0fdfa' : '#fff',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
            {errors.visitType && <div className="error" style={{ marginTop: 10 }}>{errors.visitType}</div>}
          </>
        )}

        {step === 3 && (
          <>
            <h3 style={{ marginTop: 0 }}>3 · Patient</h3>
            <div className="form-grid">
              <div className="field full">
                <label>Search existing patient</label>
                <input
                  className="input"
                  placeholder="Name, phone or UHID…"
                  onChange={(e) => searchPatients(e.target.value)}
                />
                {searching && <div className="hint">Searching…</div>}
                {patientHits.length > 0 && (
                  <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    {patientHits.map((p) => (
                      <button
                        key={p.patientId || p.phone || p.name}
                        type="button"
                        onClick={() => pickPatient(p)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
                          border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff', cursor: 'pointer',
                        }}
                      >
                        <strong>{p.name || p.patientName}</strong>
                        <span className="hint"> · {p.patientId || 'new'} · {p.phone || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="field">
                <label>Patient name *</label>
                <input className="input" value={form.patient} onChange={(e) => handleChange('patient', e.target.value)} placeholder="Or add new patient" />
                {errors.patient && <div className="error">{errors.patient}</div>}
              </div>
              <div className="field">
                <label>Phone *</label>
                <input className="input" maxLength={10} value={form.phone} onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, ''))} />
                {errors.phone && <div className="error">{errors.phone}</div>}
              </div>
              <div className="field">
                <label>UHID (optional)</label>
                <input className="input" value={form.uhid} onChange={(e) => handleChange('uhid', e.target.value)} placeholder="Leave blank to auto-create" />
              </div>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h3 style={{ marginTop: 0 }}>4 · Confirm & book</h3>
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 14, fontSize: 14 }}>
              <div><strong>Doctor:</strong> {form.doctor || '—'}</div>
              <div><strong>When:</strong> {form.date || '—'} · {form.timeSlot ? formatDisplayTime(form.timeSlot) : '—'}</div>
              <div><strong>Type:</strong> {form.visitType}</div>
              <div><strong>Patient:</strong> {form.patient || '—'} · {form.phone || '—'}{form.uhid ? ` · ${form.uhid}` : ''}</div>
            </div>
            <div className="field">
              <label>Chief complaint</label>
              <input className="input" value={form.complaint} onChange={(e) => handleChange('complaint', e.target.value)} placeholder="Reason for visit" />
              {errors.complaint && <div className="error">{errors.complaint}</div>}
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" disabled={step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))}>Back</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={() => { setForm(EMPTY_FORM); setErrors({}); setSuccess(''); setStep(1); }}>Reset</button>
            {step < 4 ? (
              <button type="button" className="btn btn-primary" onClick={goNext}>Continue</button>
            ) : (
              <button type="button" className="btn btn-primary" disabled={busy} onClick={handleBook}>
                {busy ? 'Booking…' : 'Book appointment'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Tab 3: Week View
// ─────────────────────────────────────────────

function WeekTab({ appointments, doctors = [] }) {
  const weekDays = useMemo(() => buildWeekDays(), []);
  const docList = doctors.length ? doctors : [];
  const weeklyData = useMemo(() => {
    const data = {};
    docList.forEach((d) => { data[d.name] = weekDays.map(() => 0); });
    appointments.forEach((apt) => {
      const dayIdx = weekDays.findIndex((w) => w.dateStr === apt.date);
      if (dayIdx >= 0 && data[apt.doctor]) {
        data[apt.doctor][dayIdx] = (data[apt.doctor][dayIdx] || 0) + 1;
      }
    });
    return data;
  }, [appointments, weekDays, docList]);

  const dayTotals = useMemo(() => {
    return weekDays.map((_, di) =>
      docList.reduce((sum, d) => sum + (weeklyData[d.name]?.[di] || 0), 0)
    );
  }, [weeklyData, weekDays, docList]);

  const today = getTodayStr();

  return (
    <div>
      <div className="info-banner" style={{ marginBottom: 20 }}>
        <span style={{ marginRight: 6 }}>📆</span>
        Weekly appointment overview — <strong>{weekRangeLabel(weekDays)}</strong>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(130px, 1fr))', gap: 10, minWidth: 800 }}>
          {weekDays.map((day, di) => {
            const isToday = day.dateStr === today;
            return (
              <div key={day.dateStr} style={{
                borderRadius: 12,
                border: isToday ? '2px solid #6366f1' : '1.5px solid #e2e8f0',
                background: isToday ? '#f5f3ff' : '#fff',
                overflow: 'hidden',
                boxShadow: isToday ? '0 4px 16px #6366f120' : '0 1px 4px #0001',
              }}>
                {/* Day Header */}
                <div style={{
                  padding: '10px 12px',
                  background: isToday ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                  borderBottom: '1px solid #e2e8f0',
                  textAlign: 'center',
                }}>
                  <div style={{ fontWeight: 800, fontSize: 15, color: isToday ? '#fff' : '#1e293b' }}>{day.label}</div>
                  <div style={{ fontSize: 12, color: isToday ? '#c7d2fe' : '#64748b', marginTop: 2 }}>{day.date}</div>
                  {isToday && <div style={{ fontSize: 10, background: '#fff3', color: '#fff', borderRadius: 6, padding: '1px 6px', marginTop: 3, display: 'inline-block', fontWeight: 700 }}>TODAY</div>}
                </div>

                {/* Doctor counts */}
                <div style={{ padding: '10px 10px 6px' }}>
                  {docList.map(doc => {
                    const count = weeklyData[doc.name]?.[di] || 0;
                    return (
                      <div key={doc.name} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '5px 8px', borderRadius: 7, marginBottom: 5,
                        background: `${doc.color}12`, border: `1px solid ${doc.color}30`,
                      }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: doc.color, lineHeight: 1.2 }}>{doc.name.replace('Dr. ', '')}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1 }}>{doc.dept.substring(0, 10)}</div>
                        </div>
                        <span style={{
                          background: count === 0 ? '#f1f5f9' : doc.color,
                          color: count === 0 ? '#94a3b8' : '#fff',
                          borderRadius: '50%', width: 24, height: 24,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800, fontSize: 12, flexShrink: 0,
                        }}>{count}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Day Total */}
                <div style={{
                  padding: '8px 12px',
                  background: isToday ? '#ede9fe' : '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Total</span>
                  <span style={{ fontWeight: 800, fontSize: 16, color: isToday ? '#6366f1' : '#1e293b' }}>{dayTotals[di]}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 20, padding: '14px 18px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', alignSelf: 'center' }}>LEGEND:</span>
        {docList.map(d => (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: d.color, display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#475569' }}>{d.name} <span style={{ color: '#94a3b8' }}>— {d.dept}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Tab 4: Doctor Settings
// ─────────────────────────────────────────────

function DoctorSettingsTab({ hospitalDoctors }) {
  const { config, save: saveConfig } = useHmsConfig('doctor-schedule');
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  const baseList = hospitalDoctors && hospitalDoctors.length ? hospitalDoctors : [];

  React.useEffect(() => {
    const persisted = config?.doctors;
    const withDefaults = (list) => list.map((d) => ({
      ...d,
      holiday: !!d.holiday,
      morning: d.morning || '09:00–13:00',
      evening: d.evening || '16:00–20:00',
      maxPatients: Number(d.maxPatients) > 0 ? Number(d.maxPatients) : 30,
    }));
    if (persisted && persisted.length) {
      setSettings(withDefaults(persisted));
    } else {
      setSettings(withDefaults(baseList.map((d) => ({ ...d, holiday: false }))));
    }
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveSettings = settings || baseList.map((d) => ({
    ...d,
    holiday: false,
    morning: d.morning || '09:00–13:00',
    evening: d.evening || '16:00–20:00',
    maxPatients: Number(d.maxPatients) > 0 ? Number(d.maxPatients) : 30,
  }));

  function updateMax(idx, val) {
    setSettings(prev => (prev || effectiveSettings).map((s, i) => i === idx ? { ...s, maxPatients: Math.max(1, Number(val)) } : s));
  }

  function toggleHoliday(idx) {
    setSettings(prev => (prev || effectiveSettings).map((s, i) => i === idx ? { ...s, holiday: !s.holiday } : s));
  }

  async function handleSave() {
    setBusy(true);
    setErr('');
    try {
      await saveConfig({ doctors: effectiveSettings });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="info-banner" style={{ marginBottom: 20 }}>
        <span style={{ marginRight: 6 }}>⚙️</span>
        Manage patient load limits and holidays here. Appointment slot times come from Rx pad settings → Doctor availability.
      </div>
      {err && <div className="error mb">{err}</div>}

      <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px #0001' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)' }}>
              {['Doctor', 'Department', 'Morning Hours', 'Evening Hours', 'Max Patients / Day', 'Holiday'].map(col => (
                <th key={col} style={{ padding: '13px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {effectiveSettings.map((doc, idx) => (
              <tr key={doc.name} style={{ background: doc.holiday ? '#fef2f2' : (idx % 2 === 0 ? '#fff' : '#f8fafc'), opacity: doc.holiday ? 0.65 : 1, transition: 'all 0.2s' }}>

                {/* Doctor name with color dot */}
                <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 36, height: 36, borderRadius: '50%', background: doc.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                      {doc.name.split(' ').filter(w => w !== 'Dr.').map(w => w[0]).join('').slice(0, 2)}
                    </span>
                    <div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>{doc.name}</div>
                      {doc.holiday && <span style={{ fontSize: 11, background: '#fee2e2', color: '#b91c1c', borderRadius: 6, padding: '1px 6px', fontWeight: 700 }}>ON HOLIDAY</span>}
                    </div>
                  </div>
                </td>

                {/* Dept */}
                <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ background: `${doc.color}15`, color: doc.color, padding: '3px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: `1px solid ${doc.color}30` }}>
                    {doc.dept}
                  </span>
                </td>

                {/* Morning Hours */}
                <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', color: '#475569', fontWeight: 500 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontSize: 14 }}>🌅</span>
                    {doc.morning}
                  </span>
                </td>

                {/* Evening Hours */}
                <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', color: '#475569', fontWeight: 500 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontSize: 14 }}>🌇</span>
                    {doc.evening}
                  </span>
                </td>

                {/* Max Patients */}
                <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={doc.maxPatients}
                      disabled={doc.holiday}
                      onChange={e => updateMax(idx, e.target.value)}
                      style={{
                        width: 70, padding: '6px 10px', borderRadius: 8,
                        border: '1.5px solid #e2e8f0', fontSize: 14, fontWeight: 700,
                        color: '#1e293b', textAlign: 'center', outline: 'none',
                        cursor: doc.holiday ? 'not-allowed' : 'text',
                        background: doc.holiday ? '#f1f5f9' : '#fff',
                      }}
                      onFocus={e => { if (!doc.holiday) e.target.style.borderColor = doc.color; }}
                      onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                    />
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>/ day</span>
                  </div>
                </td>

                {/* Holiday Toggle */}
                <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                    {/* Custom Toggle */}
                    <span
                      onClick={() => toggleHoliday(idx)}
                      style={{
                        display: 'inline-flex', width: 44, height: 24, borderRadius: 12,
                        background: doc.holiday ? '#ef4444' : '#e2e8f0',
                        position: 'relative', transition: 'background 0.25s', cursor: 'pointer', flexShrink: 0,
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: 3, left: doc.holiday ? 23 : 3,
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        boxShadow: '0 1px 4px #0003', transition: 'left 0.25s',
                      }} />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: doc.holiday ? '#b91c1c' : '#64748b' }}>
                      {doc.holiday ? 'Holiday' : 'Available'}
                    </span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={handleSave}>{busy ? 'Saving…' : '✓ Save Settings'}</button>
        {saved && <span style={{ color: '#10b981', fontWeight: 600, fontSize: 13 }}>✓ Settings saved</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Tab: Dental Chair Board (dental doctors only)
// ─────────────────────────────────────────────

function DentalChairsTab({ appointments, dentalDoctors, onBook }) {
  const [date, setDate] = useState(getTodayStr());
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState({ patient: '', phone: '', doctor: '' });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState('');

  const docList = dentalDoctors.length ? dentalDoctors : [];

  const dayApts = useMemo(() => {
    const dentalNames = new Set(docList.map((d) => d.name));
    return appointments.filter((a) => a.date === date && (!dentalNames.size || dentalNames.has(a.doctor)));
  }, [appointments, date, docList]);

  const chairGrid = useMemo(() => {
    const grid = {};
    DENTAL_CHAIRS.forEach((ch) => {
      grid[ch] = {};
      DENTAL_SLOT_TIMES.forEach((t) => { grid[ch][t] = null; });
    });
    dayApts.forEach((apt) => {
      const time = apt.time?.slice(0, 5);
      if (!DENTAL_SLOT_TIMES.includes(time)) return;
      const chair = apt.chair && DENTAL_CHAIRS.includes(apt.chair) ? apt.chair : null;
      if (chair) {
        grid[chair][time] = apt;
        return;
      }
      const free = DENTAL_CHAIRS.find((ch) => !grid[ch][time]);
      if (free) grid[free][time] = { ...apt, chair: free, autoAssigned: true };
    });
    return grid;
  }, [dayApts]);

  const openBook = (chair, time) => {
    setDraft({ chair, time });
    setForm({ patient: '', phone: '', doctor: docList[0]?.name || '' });
    setSuccess('');
  };

  const submitBook = async () => {
    if (!draft || !form.patient.trim() || form.phone.length < 10 || !form.doctor) return;
    setBusy(true);
    setSuccess('');
    try {
      await onBook({
        patient: form.patient.trim(),
        phone: form.phone,
        doctor: form.doctor,
        date,
        time: draft.time,
        type: 'Walk-in',
        complaint: 'Dental OP',
        chair: draft.chair,
      });
      setSuccess(`Booked ${form.patient} · ${draft.chair} · ${formatDisplayTime(draft.time)}`);
      setDraft(null);
    } catch {
      /* parent shows error */
    } finally {
      setBusy(false);
    }
  };

  if (!docList.length) {
    return (
      <div className="card" style={{ padding: 20 }}>
        <p style={{ margin: 0, color: '#64748b' }}>No dental doctors in staff directory. Add a doctor with dental specialty and enable Dental OP workspace in Hospital Admin.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="info-banner" style={{ marginBottom: 16 }}>
        Dental chair board — assign patients to <strong>Chair 1–3</strong> by time slot (morning & evening blocks).
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 700, fontSize: 13 }}>Date</label>
        <input className="input" type="date" style={{ width: 180 }} value={date} onChange={(e) => setDate(e.target.value)} />
        <span className="hint">{dayApts.length} dental appointment(s) this day</span>
      </div>

      {success && (
        <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '12px 16px', color: '#166534', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>
          {success}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ padding: 8, textAlign: 'left', background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>Time</th>
              {DENTAL_CHAIRS.map((ch) => (
                <th key={ch} style={{ padding: 8, textAlign: 'left', background: '#f0fdfa', borderBottom: '2px solid #99f6e4', color: '#0d6e6a' }}>{ch}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DENTAL_SLOT_TIMES.map((time) => (
              <tr key={time}>
                <td style={{ padding: 8, fontWeight: 700, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{formatDisplayTime(time)}</td>
                {DENTAL_CHAIRS.map((ch) => {
                  const apt = chairGrid[ch]?.[time];
                  return (
                    <td key={ch} style={{ padding: 6, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                      {apt ? (
                        <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '8px 10px' }}>
                          <div style={{ fontWeight: 700 }}>{apt.patient}</div>
                          <div className="hint" style={{ fontSize: 11 }}>{apt.doctor}</div>
                          {apt.autoAssigned && <div className="hint" style={{ fontSize: 10 }}>Unassigned chair</div>}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openBook(ch, time)}
                          style={{
                            width: '100%', padding: '10px 8px', borderRadius: 8, border: '1.5px dashed #cbd5e1',
                            background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 12,
                          }}
                        >
                          + Book
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {draft && (
        <div className="card" style={{ marginTop: 20, padding: 18, borderLeft: '4px solid #0d6e6a' }}>
          <h4 style={{ marginTop: 0 }}>Book · {draft.chair} · {formatDisplayTime(draft.time)}</h4>
          <div className="form-grid">
            <div className="field">
              <label>Dental doctor *</label>
              <select className="input" value={form.doctor} onChange={(e) => setForm((f) => ({ ...f, doctor: e.target.value }))}>
                {docList.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Patient name *</label>
              <input className="input" value={form.patient} onChange={(e) => setForm((f) => ({ ...f, patient: e.target.value }))} />
            </div>
            <div className="field">
              <label>Phone *</label>
              <input className="input" maxLength={10} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, '') }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setDraft(null)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busy || form.phone.length < 10 || !form.patient.trim()} onClick={submitBook}>
              {busy ? 'Booking…' : 'Confirm booking'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export default function AppointmentsPage() {
  const { user } = useAuth();
  const weekDays = useMemo(() => buildWeekDays(), []);
  const { data: dir } = useHospitalDirectory();
  const { settings: emrSettings, error: emrSettingsError, reload: reloadEmrSettings } = useEmrSettings();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getQueue({ limit: 120 }), []);
  const [activeTab, setActiveTab] = useState('today');
  const [actionErr, setActionErr] = useState('');

  const dentalDoctors = useMemo(() => {
    const list = (dir?.doctors || []).filter((d) => (
      isDentalSpecialty(d.specialty) || isDentalSpecialty(d.department) || d.enableDentalOp
    ));
    return list.map((d, i) => ({
      name: d.name,
      dept: d.specialty || d.department || 'Dental',
      color: ['#0d6e6a', '#14b8a6', '#2dd4bf'][i % 3],
    }));
  }, [dir]);

  const showDentalChairs = useMemo(() => (
    doctorHasDentalWorkspace(user, emrSettings) || dentalDoctors.length > 0
  ), [user, emrSettings, dentalDoctors]);

  const hospitalDoctors = useMemo(() => {
    const list = (dir?.doctors || []).map((d, i) => ({
      name: d.name,
      dept: d.specialty || d.department || 'OPD',
      color: ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6'][i % 5],
    }));
    return list.length ? list : [];
  }, [dir]);

  const appointments = useMemo(
    () => (data?.queue || []).map(queueRowToAppointment),
    [data],
  );

  const tabs = useMemo(() => {
    const base = [
      { key: 'today',    label: "Today's Appointments" },
      { key: 'book',     label: 'Book Appointment'      },
      { key: 'week',     label: 'Week View'              },
    ];
    if (showDentalChairs) {
      base.push({ key: 'dental-chairs', label: '🦷 Dental Chairs' });
    }
    base.push({ key: 'settings', label: 'Doctor Settings' });
    return base;
  }, [showDentalChairs]);

  async function handleStatusChange(id, newStatus) {
    setActionErr('');
    try {
      await ipApi.patchQueue(id, queueStatusFromApt(newStatus));
      await reload();
    } catch (e) {
      setActionErr(e.message);
    }
  }

  async function handleBook(newApt) {
    setActionErr('');
    const defaultFee = dir?.hospital?.pricing?.opConsultFee || 500;
    try {
      // Resolve patient ID: use provided UHID, or auto-create a patient record for new patients
      let patientId = newApt.uhid && newApt.uhid !== 'NEW' ? newApt.uhid.trim() : '';
      if (!patientId) {
        try {
          const pr = await ipApi.createPatient({
            name: newApt.patient,
            phone: newApt.phone,
            hospitalId: user?.hospitalId,
          });
          patientId = pr?.patient?.patientId || '';
        } catch {
          // non-fatal — queue entry proceeds without a linked UHID
        }
      }
      await ipApi.addToQueue({
        patientName: newApt.patient,
        patientId,
        patientPhone: newApt.phone,
        doctor: newApt.doctor,
        fee: defaultFee,
        reason: newApt.complaint,
        visitDate: newApt.date,
        visitType: newApt.type,
        appointmentTime: newApt.time,
        hospitalId: user?.hospitalId,
        status: Q.BOOKED,
        chair: newApt.chair || '',
        service: newApt.chair ? newApt.chair : 'Consultation',
      });
      await reload();
    } catch (e) {
      setActionErr(e.message);
      throw e;
    }
  }

  return (
    <div style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif", padding: '24px', maxWidth: 1300, margin: '0 auto' }}>

      {/* Page Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, boxShadow: '0 4px 12px #6366f133' }}>
            🏥
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Appointments</h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: 14, marginTop: 2 }}>Hospital Management System — OPD Module</p>
          </div>
        </div>
      </div>

      {emrSettingsError && (
        <div className="error mb" style={{ marginBottom: 16 }}>
          Could not load appointment / slot settings — {emrSettingsError}{' '}
          <button type="button" className="btn btn-sm btn-secondary" onClick={reloadEmrSettings}>Retry</button>
        </div>
      )}
      {!hospitalDoctors.length && (
        <div className="info-banner mb" style={{ marginBottom: 16 }}>
          No doctors found in hospital staff directory. Add doctors in Hospital Admin → Staff to enable scheduling.
        </div>
      )}

      {/* Tab Navigation */}
      <div className="period-tabs" style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 12, padding: 5, marginBottom: 28, flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1, minWidth: 150, padding: '10px 18px',
              borderRadius: 9, border: 'none', cursor: 'pointer',
              fontWeight: activeTab === tab.key ? 700 : 500,
              fontSize: 13,
              background: activeTab === tab.key ? '#fff' : 'transparent',
              color: activeTab === tab.key ? '#6366f1' : '#64748b',
              boxShadow: activeTab === tab.key ? '0 2px 8px #0002' : 'none',
              transition: 'all 0.2s', whiteSpace: 'nowrap', fontFamily: 'inherit',
            }}
            onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.color = '#6366f1'; }}
            onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.color = '#64748b'; }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {(error || actionErr) && (
        <div className="error mb">{error || actionErr}</div>
      )}
      {loading && activeTab === 'today' && <div className="hint mb">Loading appointments from live queue…</div>}

      {/* Tab Content */}
      <div>
        {activeTab === 'today'    && <TodayTab appointments={appointments} onStatusChange={handleStatusChange} />}
        {activeTab === 'book'     && <BookTab onBook={handleBook} doctors={hospitalDoctors} appointments={appointments} emrSettings={emrSettings} />}
        {activeTab === 'week'     && <WeekTab appointments={appointments.filter(a => weekDays.some(w => w.dateStr === a.date))} doctors={hospitalDoctors} />}
        {activeTab === 'dental-chairs' && showDentalChairs && (
          <DentalChairsTab appointments={appointments} dentalDoctors={dentalDoctors} onBook={handleBook} />
        )}
        {activeTab === 'settings' && <DoctorSettingsTab hospitalDoctors={hospitalDoctors} />}
      </div>
    </div>
  );
}
